import {Stack} from '@mantine/core';
import React from 'react';

import {makeAnalyticsEventId} from 'analytics/Analytics';
import DataConnector from 'connect/DataConnector';
import {StorableItemType} from 'constants/ModelTypeConstants';
import {Registry} from 'constants/RegistryConstants';
import {SelectedParentStorable} from 'constants/SampleConstants';
import {SampleStatusItem} from 'constants/SampleStatusConstants';
import {
  canProceedWithSamples,
  checkForSampleStatusWarnings,
  SampleStatusWarning,
} from 'inventory/sample-status/SampleStatusWarning';
import {SampleStatusWarningTrackingParams} from 'inventory/sample-status/SampleStatusWarningTracking';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import RegistrySelector from 'registry/RegistrySelector';
import ParentStorableDataSelector from 'samples/ParentStorableDataSelector';
import * as SampleUtil from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import {restStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import {SubmitSelectedParentHandler} from 'samples/storables/ParentStorableTypes';
import {getEmptySelectedParent} from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import APIUtil from 'util/APIUtil';

export interface SelectParentStorableModalProps {
  modalTitleText: string;
  onRequestClose: () => void;
  onSubmit?: SubmitSelectedParentHandler;
  /** If true, modal will wait for onFinish to be called from inside `onSetParentStorable` before closing.
   * If called with an error message, modal will remain up to render that error. Otherwise, it will close.
   * If false, modal will close immediately on submit and any errors will have to be surfaced via toasts. */
  waitForSubmitErrors?: boolean;
  parentStorableTypesOverride?: Array<StorableItemType>;
  storableSchema?: {id: string; registryId: string; name: string} | null;
  showRegistrySelector?: boolean;
  submitText: string;
  sampleStatusWarningParams?: Omit<SampleStatusWarningTrackingParams, 'eventGroupId'>;
  initialSelectedParentId?: string | null;
  allowNone?: boolean;
}

interface EnhancedSelectParentStorableModalProps {
  initialSelectedParent?: SelectedParentStorable;
}

interface State {
  selectedParent: SelectedParentStorable;
  selectedRegistryId: string | null;
  canProceedOnSampleStatus: boolean;
  selectedSampleIds: Array<string>;
  shouldShowStatusWarnings: boolean;
  samples: Array<SampleStatusItem> | null;
  sampleStatusWarningsLoading: boolean;
  eventGroupId: string;
  isSubmitting: boolean;
  submitErrorMessage: string | null;
}

class SelectParentStorableModalConnector extends DataConnector<
  SelectParentStorableModalProps,
  EnhancedSelectParentStorableModalProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps {
    if (this.props.initialSelectedParentId == null) {
      return this.props;
    }

    const hasInitialSelectedParentStorableSummaryExtras = this.storableStore()
      .getStorableSummaryExtras()
      .ancestralStorableIdsByStorableId?.hasOwnProperty(this.props.initialSelectedParentId);

    if (!hasInitialSelectedParentStorableSummaryExtras) {
      StorableSummaryExtrasManager.requestFetch(this.props.initialSelectedParentId);
    }

    const initialSelectedParentStorableSummary = this.storableStore().getStorableSummary(
      this.props.initialSelectedParentId
    );
    if (initialSelectedParentStorableSummary == null) {
      StorableActions.bulkFetchSummaries([this.props.initialSelectedParentId]);
    }

    if (initialSelectedParentStorableSummary != null && hasInitialSelectedParentStorableSummaryExtras) {
      const initialSelectedParent: SelectedParentStorable = {
        storable: restStorableSummaryToGraphQL(initialSelectedParentStorableSummary),
        gridCells: [],
      };
      return {
        ...this.props,
        initialSelectedParent,
      };
    } else {
      return this.props;
    }
  }
}

export class SelectParentStorableModal extends React.Component<
  SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps,
  State
> {
  override state: State = {
    selectedParent: getEmptySelectedParent(),
    selectedRegistryId: null,
    canProceedOnSampleStatus: false,
    selectedSampleIds: [],
    shouldShowStatusWarnings: false,
    samples: null,
    sampleStatusWarningsLoading: this.props.sampleStatusWarningParams != null,
    eventGroupId: makeAnalyticsEventId(),
    isSubmitting: false,
    submitErrorMessage: null,
  };

  async checkForWarnings(): Promise<void> {
    const sampleIds = this.props.sampleStatusWarningParams?.sampleIds ?? [];
    const {shouldShowWarning, samples} = await checkForSampleStatusWarnings({
      sampleIds,
    });
    this.setState({shouldShowStatusWarnings: shouldShowWarning, samples: samples ?? null});
    this.setSelectedSampleIds(sampleIds);
    this.setState({sampleStatusWarningsLoading: false});
  }

  private setCanProceedOnSampleStatus = (override: boolean): void => {
    this.setState({canProceedOnSampleStatus: override});
  };

  private setSelectedSampleIds = (sampleIds: Array<string>): void => {
    this.setState({selectedSampleIds: sampleIds});
  };

  private setSelectedParent = (selectedParent: SelectedParentStorable): void => {
    this.setState({selectedParent});
  };

  private handleRegistryChange = (e: {target: {value: Registry | null}}): void => {
    const newRegistryId = e.target.value?.id ?? null;
    this.setState((prevState) => {
      if (prevState.selectedRegistryId === newRegistryId) {
        return null;
      }
      const isInitialSelection = prevState.selectedRegistryId == null;
      return {
        selectedRegistryId: newRegistryId,
        selectedParent: isInitialSelection ? prevState.selectedParent : getEmptySelectedParent(),
      };
    });
  };

  override componentDidMount(): void {
    if (this.props.sampleStatusWarningParams != null) {
      this.checkForWarnings();
    }
    if (this.state.selectedParent.storable == null && this.props.initialSelectedParent != null) {
      this.setSelectedParent(this.props.initialSelectedParent);
    }
  }

  override componentDidUpdate(
    prevProps: Readonly<SelectParentStorableModalProps & EnhancedSelectParentStorableModalProps>
  ): void {
    if (
      this.state.selectedParent.storable == null &&
      this.props.initialSelectedParent &&
      (prevProps.initialSelectedParent == null ||
        prevProps.initialSelectedParentId !== this.props.initialSelectedParentId)
    ) {
      this.setSelectedParent(this.props.initialSelectedParent);
    }
  }

  private isLoading(): boolean {
    return (
      this.state.sampleStatusWarningsLoading ||
      (this.props.initialSelectedParentId != null && this.props.initialSelectedParent == null)
    );
  }

  private shouldDisableSave(): boolean {
    const {storable, gridCells} = this.state.selectedParent;
    return (
      (storable ? SampleUtil.isGridId(storable.id) && gridCells.length === 0 : !this.props.allowNone) ||
      this.isLoading() ||
      this.state.isSubmitting ||
      !canProceedWithSamples(
        this.state.selectedSampleIds,
        this.state.shouldShowStatusWarnings,
        this.state.canProceedOnSampleStatus
      )
    );
  }

  private handleSubmit = (): void => {
    const {onSubmit} = this.props;
    if (onSubmit) {
      this.setState({isSubmitting: true, submitErrorMessage: null});
      onSubmit(
        this.state.selectedParent,
        this.state.eventGroupId,
        this.props.waitForSubmitErrors ? this.handleCompleteSubmitAttempt : undefined,
        this.props.waitForSubmitErrors ? this.handleSubmitCancel : undefined
      );
    }
    if (!this.props.waitForSubmitErrors) {
      this.handleCancel();
    }
  };

  private handleCompleteSubmitAttempt = (errorMessage?: string): void => {
    // Close from the setState callback, which React never runs for an unmounted instance. The submit can
    // outlive this modal, and onRequestClose is shared with any modal the user reopens in the meantime.
    this.setState({isSubmitting: false, submitErrorMessage: errorMessage ?? null}, () => {
      if (!errorMessage) {
        this.props.onRequestClose();
      }
    });
  };

  /** Called when the caller cancels the submit before the change is performed. Modal stays open so the user
   * can adjust the parent and submit again.
   */
  private handleSubmitCancel = (): void => {
    this.setState({isSubmitting: false});
  };

  handleCancel = (): void => {
    this.props.onRequestClose();
  };

  private renderPossibleWarning = (): JSX.Element | null => {
    if (this.props.sampleStatusWarningParams == null) {
      return null;
    }
    const {sampleIds, ...statusWarningTrackingParams} = this.props.sampleStatusWarningParams;
    return this.state.shouldShowStatusWarnings && this.state.samples != null ? (
      <SampleStatusWarning
        sampleIds={sampleIds}
        selectedSampleIds={this.state.selectedSampleIds}
        setSelectedSampleIds={this.setSelectedSampleIds}
        shouldProceed={this.state.canProceedOnSampleStatus}
        setShouldProceed={this.setCanProceedOnSampleStatus}
        trackingParams={{...statusWarningTrackingParams, eventGroupId: this.state.eventGroupId}}
        samples={this.state.samples}
      />
    ) : null;
  };

  override render(): JSX.Element {
    return (
      <Modal
        title={this.props.modalTitleText}
        onRequestClose={this.handleCancel}
        onRequestSubmit={this.handleSubmit}
        buttons={[
          {
            text: this.state.isSubmitting ? 'Saving...' : this.props.submitText,
            disabled: this.shouldDisableSave(),
            submit: true,
            style: ButtonStyle.PRIMARY,
          },
        ]}
      >
        {this.isLoading() ? (
          <CenteredHorizontalThrobber />
        ) : (
          <>
            {this.renderPossibleWarning()}
            {canProceedWithSamples(
              this.state.selectedSampleIds,
              this.state.shouldShowStatusWarnings,
              this.state.canProceedOnSampleStatus
            ) && (
              <Stack spacing="sm">
                {this.props.showRegistrySelector && (
                  <div>
                    <FieldLabel label="Registry" />
                    <RegistrySelector
                      registryId={this.state.selectedRegistryId}
                      onChange={this.handleRegistryChange}
                    />
                  </div>
                )}
                <ParentStorableDataSelector
                  onUpdate={this.setSelectedParent}
                  autoFocus={true}
                  parentStorableTypesOverride={this.props.parentStorableTypesOverride}
                  selectedParent={this.state.selectedParent}
                  storableSchema={this.props.storableSchema}
                  registryId={
                    this.props.showRegistrySelector ? (this.state.selectedRegistryId ?? undefined) : undefined
                  }
                  allowNone={this.props.allowNone}
                  // storablesToBeMoved is undefined for locations since they don't count against available space
                  storablesToBeMoved={
                    this.props.storableSchema && APIUtil.isLocationSchema(this.props.storableSchema.id)
                      ? undefined
                      : [{storableSchema: this.props.storableSchema ?? undefined}]
                  }
                />
              </Stack>
            )}
            {this.state.submitErrorMessage && (
              <InlineBanner bannerType={InlineBannerType.ERROR} text={this.state.submitErrorMessage} />
            )}
          </>
        )}
      </Modal>
    );
  }
}

const ConnectedSelectParentStorableModal =
  SelectParentStorableModalConnector.connect(SelectParentStorableModal);
export default ConnectedSelectParentStorableModal;
